// All response types consolidated in one file
export enum Attendance {
    yes = "yes",
    no = "no",
    maybe = "maybe",
    unknown = "unknown",
}

export enum EventType {
    private = 0,
    open = 1,
}

export enum Occurrence {
    disable = "disable",
    daily = "daily",
    weekly = "weekly",
    monthly = "monthly",
}

export enum Sku {
    free = 0,
    plus = 1,
}

export enum Plan {
    development = "development",
    anonymous = "anonymous",
    free = "free",
    tester = "tester",
    plus = "plus",
    plusTrial = "plusTrial",
}

export enum ActivityType {
    eventUpdated = "eventUpdated",
}

export interface EventFeatures {
    hideGuestList?: boolean;
    hideGuestCount?: boolean;
    disableRsvp?: boolean;
    disablePosts?: boolean;
    disableComments?: boolean;
    disablePhotoUpload?: boolean;
    smsRsvp?: boolean;
}

export interface GuestCounts {
    yes: number;
    no: number;
    maybe: number;
    unknown: number;
}

export interface PubUser {
    id: string; // UUID
    accessCode?: string;
    name: string;
    photoUrl?: string;
}

export interface PubGuest {
    id: string; // UUID
    name: string;
    attendance: Attendance;
    plusOnes: number;
    hasApp?: boolean; // Made optional to match original definition
    eventId?: string;
    photoUrl?: string;
    isHost: boolean;
    createTime: number;
    invitedBy?: string; // UUID
}

export interface Place {
    latitude: number;
    longitude: number;
}

export interface PubEvent {
    id: string;
    name: string;
    description: string;
    location: string;
    place?: Place;
    time: number;
    endTime?: number;
    owner: PubUser;
    requestedBy?: string; // UUID
    myAttendance: Attendance;
    attendees: PubGuest[];
    hosts: PubGuest[];
    coverPhotoUrl?: string;
    guestInviteLimit?: number;
    type: EventType;
    emojiWallpaper?: string;
    archived: boolean;
    cancelled: boolean;
    occurring?: Occurrence;
    notifiable?: boolean;
    customYes?: string;
    customNo?: string;
    customMaybe?: string;
    customRSVP?: string;
    giftLink?: string;
    giftKeywords?: string;
    guestCounts?: GuestCounts;
    sku: Sku;
    features: EventFeatures;
    hasGreetingCard?: boolean;
    timezone?: string;
}

export interface PubComment {
    id: string; // UUID
    userId: string; // UUID
    username: string;
    userPhotoUrl?: string;
    body: string;
    createTime: number;
    reactions: PubReactionSummary[];
}

export interface PubPost {
    id: string; // UUID
    userId: string; // UUID
    username: string;
    userPhotoUrl?: string;
    body: string;
    createTime: number;
    comments: PubComment[];
    reactions: PubReactionSummary[];
}

export interface PubInviteLink {
    uri: string;
    userId: string;
}

export interface ApiError {
    error: boolean;
    reason: string;
    code?: number; // Unknown numeric codes remain readable through the server reason.
}

export interface DateitUserMetadata {
    smsVerificationRequired?: boolean;
    currentUsage: number;
    quota?: number;
    plan?: Plan;
    planExpires?: number;
    user: PubUser;
    email?: string;
    phone?: string;
    provider?: string;
    feedSecret: string;
}

export interface MediaUrlPayload {
    id: string; // UUID
    url: string;
}

export interface Features {
    iosOnEventRatingRequest: boolean;
    enableOpenEvents: boolean;
    enableEmojiBackground?: boolean;
    photos?: boolean;
    photoComments?: boolean;
    photoLikes?: boolean;
    plusOnes?: boolean;
    phoneAuth?: boolean;
    openEventsDefault?: boolean;
    eventActivity?: boolean;
    recurringEvents?: boolean;
    inviteFromContacts?: boolean;
    useIoLink?: boolean;
    amazonLink?: boolean;
    dateitPlus?: boolean;
}

export interface CoverPhotoManifest {
    images: PubCoverImage[];
    categories: PubCoverCategory[];
}

export interface PubCoverCategory {
    index: number;
    name: string;
    tags: string[];
}

export interface PubCoverImage {
    url: string;
    tags: string[];
}

export interface EmojiItem {
    char: string;
    name: string;
    group: string;
}

export interface PubEventMedia {
    id: string; // UUID
    mediaId: string; // UUID
    photoURL: string;
    thumbnailURL: string;
    inviteCardPhotoURL?: string;
    inviteCardthumbnailURL?: string;
    userId: string; // UUID
    username: string;
    userPhotoUrl?: string;
    createTime: number;
    commentCount: number;
    liked: boolean;
    likeCount: number;
    comments: PubComment[];
    eventId: string;
    eventName: string;
    eventTime: number;
    width: number;
    height: number;
    thumbnailWidth: number;
    thumbnailHeight: number;
}

export interface PubEventActivity {
    id: string; // UUID
    user: PubUser;
    eventUpdated?: EventUpdatedActivity;
    createTime: number;
}

export interface EventUpdatedActivity {
    oldNmae?: string; // Note: keeping the typo as it's in the original
    newName?: string;
    oldTime?: number;
    newTime?: number;
    oldEndTime?: number;
    newEndTime?: number;
    oldDescription?: string;
    newDescription?: string;
    oldLocation?: string;
    newLocation?: string;
    cancelled?: boolean;
    occurring?: Occurrence;
    nextOccurrence?: number;
}

export interface EmojiManifest {
    emojis: EmojiItem[];
    version: number;
}

export interface EmojiCategory {
    name: string;
    emojis: EmojiItem[];
}

export interface PubReactionSummary {
    emoji: string;
    users: PubUser[];
}
